import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Button,
  Typography,
  Popover,
} from "@mui/material";
import axios from "axios";
import { baseURL, dataScienceURL, otherBaseURL } from "../config";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { DialogTitleProps } from "../SideMenu/SideMenu";
import { authContext } from "../Context/Authentication/authContext";
import "./CityAttractions.scss";
import StackedBarChart from "./Components/StackedChart";
import { Bars } from "react-loader-spinner";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";

// Type for city ratings data
interface CityRating {
  Income_max_score: number;
  Retail_stores_points: number;
  city: string;
  employment_growth_points: number;
  employment_max_score: number;
  home_sales_max_score: number;
  homesales_points: number;
  hospital_max_score: number;
  hospital_points: number;
  income_points: number;
  max_points: number;
  percentage: number;
  physician_max_score: number;
  physician_points: number;
  population_max_score: number;
  population_points: number;
  retail_max_score: number;
  school_max_score: number;
  school_points: number;
  state: string;
  total_points: number;
}

export const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
    borderBottom: "1px solid #f7b047",
    borderTop: "1px solid #f7b047",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiDialogTitle-root": {
    color: "#f7b047",
    padding: "10px",
  },
  "& .MuiSvgIcon-root": {
    color: "#f7b047",
  },
  "& .MuiButton-root": {
    color: "#f7b047",
  },
  "& .MuiDialog-paper": {
    maxWidth: "44%",
    minWidth: "44%",
  },
}));

export const BootstrapDialogTitle = (props: DialogTitleProps) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

export const getAttractionText = (
  data: any,
  chartData: any,
  loading: boolean,
  ratingsData: CityRating[] = [],
  anchorEl: HTMLElement | null,
  handleInfoClick: (event: React.MouseEvent<HTMLElement>) => void,
  handlePopoverClose: () => void
) => {
  const attractionsText = data.attractions || "";

  // Extract the intro text and attractions using regex
  const introText = attractionsText.split(/(?=1\.)/)[0].trim();
  const attractionsList = attractionsText.split(/(?=\d+\.)/).slice(1);

  const thingsToDoText = data.things_to_do || "";

  // Extract the intro text and attractions using regex
  const introThingsText = thingsToDoText.split(/(?=1\.)/)[0].trim();
  const thingsToDoList = thingsToDoText.split(/(?=\d+\.)/).slice(1);

  // Format the attractions into a two-column grid
  const formattedAttractions = thingsToDoList.map((attraction, index) => (
    <Grid item xs={12} sm={6} key={index}>
      <Typography variant="h10" style={{ marginBottom: "10px" }}>
        {attraction.trim()}
      </Typography>
    </Grid>
  ));

  const formattedThingsToDo = attractionsList.map((attraction, index) => (
    <Grid item xs={12} sm={6} key={index}>
      <Typography variant="h10" style={{ marginBottom: "10px" }}>
        {attraction.trim()}
      </Typography>
    </Grid>
  ));

  // Function to get trend icon
  const getTrendIcon = (points: number, maxPoints: number) => {
    if (points === 0) {
      return (
        <TrendingDownIcon style={{ color: "#f44336", fontSize: "24px" }} />
      );
    } else if (points === maxPoints) {
      return <TrendingUpIcon style={{ color: "#4caf50", fontSize: "24px" }} />;
    } else {
      return (
        <TrendingFlatIcon style={{ color: "#ff9800", fontSize: "24px" }} />
      );
    }
  };

  // Function to render city ratings
  const renderCityRatings = () => {
    const rating = ratingsData[0];

    // Define the rating categories with their respective point and max point keys
    const ratingCategories = [
      {
        label: "Income",
        points: rating.income_points,
        maxPoints: rating.Income_max_score,
      },
      {
        label: "Retail Stores",
        points: rating.Retail_stores_points,
        maxPoints: rating.retail_max_score,
      },
      {
        label: "Employment Growth",
        points: rating.employment_growth_points,
        maxPoints: rating.employment_max_score,
      },
      {
        label: "Home Sales",
        points: rating.homesales_points,
        maxPoints: rating.home_sales_max_score,
      },
      {
        label: "Hospital",
        points: rating.hospital_points,
        maxPoints: rating.hospital_max_score,
      },
      {
        label: "Population",
        points: rating.population_points,
        maxPoints: rating.population_max_score,
      },
      {
        label: "Physicians",
        points: rating.physician_points,
        maxPoints: rating.physician_max_score,
      },
      {
        label: "Schools",
        points: rating.school_points,
        maxPoints: rating.school_max_score,
      },
    ];
    const open = Boolean(anchorEl);
    const id = open ? 'city-ratings-popover' : undefined;

  
    if (ratingsData[0].max_points && ratingsData[0].max_points < 75) {
    return (
      <div className="popup-margin">
        {/* Overall Score with Info Icon */}
        <Grid
          container
          spacing={2}
          style={{ marginTop: "10px", marginBottom: "20px" }}
        >
          <Grid item xs={12} className="popup-text-center">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <div>
                <span className="popup-subtext-heading">Overall Score</span>
                <br />
                <strong style={{ fontSize: "12px", color: "white" }}>
                Not Enough Data!
                </strong>
              </div>
              <InfoOutlinedIcon 
                style={{ 
                  color: "#f7b047", 
                  fontSize: "24px", 
                  cursor: "pointer",
                  marginTop: "10px" 
                }}
                onClick={handleInfoClick}
              />
            </div>
          </Grid>
        </Grid>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          PaperProps={{
            style: {
              maxWidth: '400px',
              padding: '16px',
              borderRadius: '8px'
            }
          }}
        >
          <div>
            {/* Legend */}
            

            {/* Rating Categories */}
            <Grid container spacing={1}>
              {ratingCategories.map((category, index) => {
                if (category.maxPoints != 0) {
                  return (
                    <Grid
                      item
                      xs={6}
                      key={index}
                      style={{ textAlign: 'center' }}
                    >
                      <div
                        style={{
                          borderRadius: "4px",
                          padding: "8px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          style={{ marginBottom: "5px", fontSize: "12px", fontWeight: "bold" }}
                        >
                          {category.label}
                        </Typography>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px"
                          }}
                        >
                          {getTrendIcon(category.points, category.maxPoints)}
                        </div>
                      </div>
                    </Grid>
                  );
                }
              })}
            </Grid>
          </div>
        </Popover>
      </div>
    );}

 


    return (
      <div className="popup-margin">
        {/* Overall Score with Info Icon */}
        <Grid
          container
          spacing={2}
          style={{ marginTop: "10px", marginBottom: "20px" }}
        >
          <Grid item xs={12} className="popup-text-center">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <div>
                <span className="popup-subtext-heading">Overall Score</span>
                <br />
                <strong style={{ fontSize: "24px", color: "white" }}>
                  {rating.percentage.toFixed(1)}%
                </strong>
              </div>
              <InfoOutlinedIcon 
                style={{ 
                  color: "#f7b047", 
                  fontSize: "24px", 
                  cursor: "pointer",
                  marginTop: "10px" 
                }}
                onClick={handleInfoClick}
              />
            </div>
          </Grid>
        </Grid>

        {/* Popover with detailed ratings */}
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          PaperProps={{
            style: {
              maxWidth: '400px',
              padding: '16px',
              borderRadius: '8px'
            }
          }}
        >
          <div>
            {/* Legend */}
            

            {/* Rating Categories */}
            <Grid container spacing={1}>
              {ratingCategories.map((category, index) => {
                if (category.maxPoints != 0) {
                  return (
                    <Grid
                      item
                      xs={6}
                      key={index}
                      style={{ textAlign: 'center' }}
                    >
                      <div
                        style={{
                          borderRadius: "4px",
                          padding: "8px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          style={{ marginBottom: "5px", fontSize: "12px", fontWeight: "bold" }}
                        >
                          {category.label}
                        </Typography>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px"
                          }}
                        >
                          {getTrendIcon(category.points, category.maxPoints)}
                        </div>
                      </div>
                    </Grid>
                  );
                }
              })}
            </Grid>
          </div>
        </Popover>
      </div>
    );
  };

  if (Object.keys(data).length || ratingsData.length > 0) {

    return (
      <div className="popup-subtext">
        {chartData && <StackedBarChart data={chartData} />}

        {/* Render City Ratings first */}
        
        {ratingsData.length > 0 && renderCityRatings()}

        <Grid
          container
          spacing={3}
          columns={{ xs: 4, sm: 8, md: 12 }}
          className="popup-margin"
        >
          {data.average_commute_time && (
            <Grid
              item
              xs={2}
              sm={4}
              md={4}
              key="average_commute_time"
              className="popup-text-center"
            >
              <span className="popup-subtext-heading">
                Average Commute Time (Min)
              </span>
              <br />
              {data.average_commute_time}
              <br />
            </Grid>
          )}
          {data.violent_crime_rate != 0 && (
            <Grid
              item
              xs={2}
              sm={4}
              md={4}
              key="violent_crime_rate"
              className="popup-text-center"
            >
              <span className="popup-subtext-heading">Violent Crime Rate (1-5)</span>
              <br />
              {data.violent_crime_rate}
              <br />
            </Grid>
          )}
          {data.property_crime_rate != 0 && (
            <Grid
              item
              xs={2}
              sm={4}
              md={4}
              key="property_crime_rate"
              className="popup-text-center"
            >
              <span className="popup-subtext-heading">
                Property Crime Rate (10-50)
              </span>
              <br />
              {data.property_crime_rate}
              <br />
            </Grid>
          )}
          {data.happiness_index && (
            <Grid
              item
              xs={2}
              sm={4}
              md={4}
              key="happiness_index"
              className="popup-text-center"
            >
              <span className="popup-subtext-heading">Happiness Index (1-10)</span>
              <br />
              {data.happiness_index} 
              <br />
            </Grid>
          )}
          {data.temperature && (
            <Grid
              item
              xs={2}
              sm={4}
              md={4}
              key="temperature"
              className="popup-text-center"
            >
              <span className="popup-subtext-heading">Temperature (°F)</span>
              <br />
              {data.temperature}
              <br />
            </Grid>
          )}
          {data.crime_index && (
            <Grid
              item
              xs={2}
              sm={4}
              md={4}
              key="crime_index"
              className="popup-text-center"
            >
              <span className="popup-subtext-heading">Crime Index (1-100)</span>
              <br />
              {data.crime_index}
              <br />
            </Grid>
          )}
        </Grid>

        {data.history && data.history !== "datanotavailable" && (
          <div>
            <span className="popup-subtext-heading">History</span>
            <br />
            {data.history}
          </div>
        )}

        {data.attractions && data.attractions !== "datanotavailable" && (
          <div className="popup-margin">
            <span className="popup-subtext-heading">Attractions</span>
            <br />
            <Grid container spacing={2}>
              {introText && (
                <Grid item xs={12}>
                  <Typography
                    variant="h10"
                    style={{ marginBottom: "20px", fontWeight: "bold" }}
                  >
                    {introText}
                  </Typography>
                </Grid>
              )}
              {formattedAttractions}
            </Grid>
          </div>
        )}

        {data.things_to_do && data.things_to_do !== "datanotavailable" && (
          <div className="popup-margin">
            <span className="popup-subtext-heading">Things To Do</span>
            <br />
            <Grid container spacing={2}>
              {introThingsText && (
                <Grid item xs={12}>
                  <Typography
                    variant="h10"
                    style={{ marginBottom: "20px", fontWeight: "bold" }}
                  >
                    {introThingsText}
                  </Typography>
                </Grid>
              )}
              {formattedThingsToDo}
            </Grid>
          </div>
        )}

        {data.nearby_cities && data.nearby_cities !== "datanotavailable" && (
          <div className="popup-margin">
            <span className="popup-subtext-heading">Nearby Cities</span>
            <br />
            {data.nearby_cities}
          </div>
        )}

        {data.higher_education_institutions &&
          data.higher_education_institutions !== "datanotavailable" && (
            <div className="popup-margin">
              <span className="popup-subtext-heading">
                Higher Education Institutions
              </span>
              <br />
              {data.higher_education_institutions}
            </div>
          )}

        {data.attraction_parks &&
          data.attraction_parks !== "datanotavailable" && (
            <div className="popup-margin">
              <span className="popup-subtext-heading">Attraction Parks</span>
              <br />
              {data.attraction_parks}
            </div>
          )}

        {data.stadiums && data.stadiums !== "datanotavailable" && (
          <div className="popup-margin">
            <span className="popup-subtext-heading">Stadiums</span>
            <br />
            {data.stadiums}
          </div>
        )}

        {data.museums && data.museums !== "datanotavailable" && (
          <div className="popup-margin">
            <span className="popup-subtext-heading">Museums</span>
            <br />
            {data.museums}
          </div>
        )}

        {data.beaches &&
          data.beaches !== "NA" &&
          data.beaches !== "datanotavailable" && (
            <div className="popup-margin">
              <span className="popup-subtext-heading">Beaches</span>
              <br />
              {data.beaches}
            </div>
          )}

        {data.major_companies &&
          data.major_companies !== "datanotavailable" && (
            <div className="popup-margin">
              <span className="popup-subtext-heading">Major Companies</span>
              <br />
              {data.major_companies}
            </div>
          )}

        {data.sports_recreational_activities &&
          data.sports_recreational_activities !== "datanotavailable" && (
            <div className="popup-margin">
              <span className="popup-subtext-heading">
                Sports Recreational Activities
              </span>
              <br />
              {data.sports_recreational_activities}
            </div>
          )}
      </div>
    );
  } else {
    if (loading) {
      return (
        <div className="popup-subtext">
          <div className="popup-margin ">
            <div className="popup-no-data">
              <Bars
                height="400"
                ariaLabel="bars-loading"
                wrapperClass="tab-spinner-progress-bar"
                visible={true}
              />
            </div>
            <br />
          </div>
        </div>
      );
    } else {
      return (
        <div className="popup-subtext">
          <div className="popup-margin ">
            <div className="popup-no-data">No Data Available</div>
            <br />
          </div>
        </div>
      );
    }
  }
};

const CityAttractions = ({}): any => {
  const { state }: any = useContext(authContext);
  const [isOpen, setOpen] = useState(false);
  const [city, setCity] = useState("");
  const [selectedCities, setSelectedCities] = useState([]);
  const [attractionData, setAttractionData]: any = useState({});
  const [chartData, setChartData] = useState<any>([]);
  const [ratingsData, setRatingsData] = useState<CityRating[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setSelectedCities(state.filters.city);
  }, [state.filters.city]);

  const handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const getCityAttractionInfo = async (cityName: string) => {
    if (cityName) {
      setLoading(true);
      const response = await axios.get(
        `${dataScienceURL}getCityAttraction?state=${state.filters.state}&city='${cityName}'`
      );
      const resp = response.data?.cube;
      const info = resp[cityName];
      if (info.length) {
        setAttractionData(info[0]);
      } else {
        setAttractionData({});
      }
      setLoading(false);
    } else {
      setAttractionData({});
      setLoading(false);
    }
  };

  const getCityPredition = async (cityName: string) => {
    if (cityName) {
      const response = await axios.get(
        `${baseURL}getCityDescriptionChartData?state=${state.filters.state}&city=${cityName}`
      );
      const groupedData: any = {};

      response.data.cube.forEach((item: any) => {
        const freq = item.freq;

        if (!groupedData[freq]) {
          groupedData[freq] = {
            freq: freq,
            homesales_percent_mean_growth: null,
            msp_percent_mean_growth: null,
            ppsf_percent_mean_growth: null,
            population_percent_mean_growth: null,
            homesaleProperties: null,
            msp_properties: null,
            ppsf_properties: null,
            population_properties: null,
          };
        }

        const { percent_high_growth, percent_low_growth } = item;

        if (item.feature === "home_sales") {
          groupedData[freq].homesales_percent_mean_growth =
            item.percent_mean_growth;
          groupedData[freq].homesaleProperties = {
            percent_high_growth,
            percent_low_growth,
          };
        } else if (item.feature === "msp") {
          groupedData[freq].msp_percent_mean_growth = item.percent_mean_growth;
          groupedData[freq].msp_properties = {
            percent_high_growth,
            percent_low_growth,
          };
        } else if (item.feature === "ppsf") {
          groupedData[freq].ppsf_percent_mean_growth = item.percent_mean_growth;
          groupedData[freq].ppsf_properties = {
            percent_high_growth,
            percent_low_growth,
          };
        } else if (item.feature === "population") {
          groupedData[freq].population_percent_mean_growth =
            item.percent_mean_growth;
          groupedData[freq].population_properties = {
            percent_high_growth,
            percent_low_growth,
          };
        }
      });

      const resultArray = Object.values(groupedData);

      const sortByFreq = (a: any, b: any) => {
        const getFrequencyNumber = (freq: any) => {
          const parts = freq.split("_");
          return parseInt(parts[0], 10);
        };

        return getFrequencyNumber(a.freq) - getFrequencyNumber(b.freq);
      };

      resultArray.sort(sortByFreq);
      setChartData(resultArray);
    }
  };

  const getCityRatings = async (cityName: string) => {
    if (cityName) {
      try {
        setLoading(true)
        const response = await axios.get(
          `${otherBaseURL}cityGrading?state=${state.filters.state}&city=${cityName}`
        );
        const resp = response.data;
        console.log(resp)
        if (resp) {
          setRatingsData(resp);
        } else {
          setRatingsData([]);
        }
        setLoading(false)
      } catch (error) {
        setLoading(false)
        console.error("Error fetching city ratings:", error);
        setRatingsData([]);
      }
    } else {
       setLoading(false)
      setRatingsData([]);
    }
  };

  useEffect(() => {
    getCityAttractionInfo(city);
    getCityPredition(city);
    getCityRatings(city);
  }, [city]);

  const handleClickOpenDialog = (val: string) => {
    if (!isOpen) {
      setOpen(true);
      setCity(val);
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setCity("");
    setRatingsData([]);
    setAnchorEl(null); // Reset popover state when closing dialog
  };

  const getCommaOrAnd = (index: number) => {
    if (selectedCities.length === 1) {
      return "";
    } else {
      if (selectedCities.length - 1 === index) {
        return " and ";
      } else {
        return ", ";
      }
    }
  };

  return (
    <>
      {selectedCities.length !== 0 && (
        <div className="popup-text">
          Click on any city name to know more about. You have selected{" "}
          {selectedCities.map((value, index) => (
            <React.Fragment key={index}>
              {index !== 0 && <span>{getCommaOrAnd(index)}</span>}
              <span
                key={index}
                className="city-name-hyperlink"
                onClick={() => handleClickOpenDialog(value)}
              >
                {" "}
                {value}
              </span>
              {index === selectedCities.length - 1 && <span>.</span>}
            </React.Fragment>
          ))}
        </div>
      )}
      <BootstrapDialog
        onClose={handleCloseDialog}
        aria-labelledby="customized-dialog-title"
        open={isOpen}
      >
        <BootstrapDialogTitle
          id="customized-dialog-title"
          onClose={handleCloseDialog}
        >
          {city} Attractions
        </BootstrapDialogTitle>
        <DialogContent dividers>
          {getAttractionText(
            attractionData, 
            chartData, 
            loading, 
            ratingsData,
            anchorEl,
            handleInfoClick,
            handlePopoverClose
          )}
        </DialogContent>
      </BootstrapDialog>
    </>
  );
};

export default CityAttractions;